import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Motion primitives.
 *
 * The brief for animation here is "you shouldn't notice it" — it exists to make
 * arrival legible (content settles into place rather than snapping), not to
 * decorate. So: short durations, small distances, a single ease-out curve, and
 * no springs. Bouncy, overshooting entrances are the fastest way to make a
 * professional tool feel like a template.
 *
 * Everything honours `prefers-reduced-motion`, which collapses these to plain
 * opacity changes.
 */

const EASE = [0.22, 0.61, 0.36, 1];

/** Content settling in on mount. */
export const FadeIn = ({ delay = 0, y = 8, children, ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={{ opacity: 0, y: reduce ? 0 : y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/**
 * A list or grid whose children arrive in sequence. The stagger is deliberately
 * tight (40ms) — enough to read as order, not enough to feel like waiting.
 */
export const Stagger = ({ children, delay = 0, className, ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="shown"
      variants={{
        hidden: {},
        shown: {
          transition: {
            staggerChildren: reduce ? 0 : 0.04,
            delayChildren: delay,
          },
        },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className, ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: reduce ? 0 : 10 },
        shown: { opacity: 1, y: 0, transition: { duration: 0.3, ease: EASE } },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/**
 * Cross-fade for a region whose contents swap in place — the section
 * workspace's stage, where switching mode replaces the whole viewer. Without
 * this the swap reads as a flicker.
 */
export const SwapIn = ({ swapKey, children, ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      key={swapKey}
      initial={{ opacity: 0, y: reduce ? 0 : 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};
